import { Injectable } from '@angular/core';
import {
  collection,
  collectionData,
  deleteDoc,
  doc,
  docData,
  Firestore,
  setDoc,
  writeBatch,
} from '@angular/fire/firestore';
import { GuideDetailInterface } from '../../core/models';
import { map, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class GuideService {
  private guideCollection: ReturnType<typeof collection>;

  constructor(private firestore: Firestore) {
    this.guideCollection = collection(this.firestore, 'guides');
  }

  // Create a new guide with a generated id
  createNewGuide(guide: Omit<GuideDetailInterface, 'id'>): Promise<void> {
    const newDocRef = doc(this.guideCollection); // Generate a new document reference with a unique id
    const id = newDocRef.id; // Extract the generated id
    // Explicitly construct the guide with the new id
    const guideWithId: GuideDetailInterface = {
      id,
      forename: guide.forename,
      surname: guide.surname,
      languages: guide.languages,
      tel: guide.tel,
      email: guide.email,
      tAndC: guide.tAndC,
    };

    return setDoc(newDocRef, guideWithId); // Save the guide with the id in Firestore
  }

  // Read all guides
  getGuides(): Observable<GuideDetailInterface[]> {
    return collectionData(this.guideCollection, {
      idField: 'id',
    }).pipe(
      map((guides) => guides as GuideDetailInterface[]),
      map((guides) =>
        guides.sort((a, b) => {
          const langCmp = a.surname.localeCompare(b.surname);
          return langCmp !== 0 ? langCmp : a.surname.localeCompare(b.surname);
        }),
      ),
    );
  }

  getGuideData(id: string): Observable<GuideDetailInterface> {
    const guideDocRef = doc(this.firestore, 'guides', id);

    return docData(guideDocRef) as Observable<GuideDetailInterface>;
  }

  // Update a guide
  updateGuide(guide: GuideDetailInterface): Promise<void> {
    const guideDoc = doc(this.firestore, `guides/${guide.id}`);
    return setDoc(guideDoc, guide);
  }

  // Delete a guide
  deleteGuide(guideId: string): Promise<void> {
    const guideDoc = doc(this.firestore, `guides/${guideId}`);
    return deleteDoc(guideDoc);
  }

  async resetTandC(guides: GuideDetailInterface[]) {
    if (guides.length === 0) return;

    const batch = writeBatch(this.firestore);
    for (const guide of guides) {
      const ref = doc(this.firestore, `guides/${guide.id}`);
      batch.update(ref, { tAndC: false });
    }

    await batch.commit();
  }
}
